﻿import { useCallback, useEffect, useState } from "react";
import { Event } from "../models/Event";
import {
	ColumnDef,
	EditingCell,
	ProcessedRow,
	SelectedCell,
} from "../models/utils";

const isValidBudgetTPHValue = (value: unknown): boolean => {
	const budgetTPH = Number(value);
	return Number.isFinite(budgetTPH) && budgetTPH > 0;
};

interface useTableCellEditingProps {
	filteredData: ProcessedRow[];
	visibleColumns: ColumnDef[];
	onCellUpdate: (params: {
		eventId: number;
		start: Date;
		end: Date | undefined;
		sectionId: number | null;
		field: keyof Event;
		value: string | number | null;
		row: ProcessedRow;
	}) => Promise<void>;
}

export const useTableCellEditing = ({
	filteredData,
	visibleColumns,
	onCellUpdate,
}: useTableCellEditingProps) => {
	const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
	const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

	const getEditableColumns = useCallback(() => {
		return visibleColumns
			.map((col, index) => ({ col, index }))
			.filter(
				({ col }) => col.editable !== undefined && col.editable !== false,
			);
	}, [visibleColumns]);

	const isCellEditable = useCallback((row: ProcessedRow, col: ColumnDef) => {
		if (row.EventId < 0) return false;
		return typeof col.editable === "function"
			? col.editable(row)
			: col.editable;
	}, []);

	const findFirstRowOfEvent = useCallback(
		(eventId: number): number => {
			for (let i = 0; i < filteredData.length; i++) {
				if (
					filteredData[i]?.EventId === eventId &&
					filteredData[i]?.IsFirstRow
				) {
					return i;
				}
			}
			// Fallback: find any row with this eventId
			for (let i = 0; i < filteredData.length; i++) {
				if (filteredData[i]?.EventId === eventId) {
					return i;
				}
			}
			return -1;
		},
		[filteredData],
	);

	const findFirstRowOfBlock = useCallback(
		(eventId: number, anyRowInBlock: number): number => {
			let firstRow = anyRowInBlock;
			for (let i = anyRowInBlock - 1; i >= 0; i--) {
				if (filteredData[i]?.EventId === eventId) {
					firstRow = i;
				} else {
					break;
				}
			}
			return firstRow;
		},
		[filteredData],
	);

	const findNextEditableCell = useCallback(
		(
			currentRowIndex: number,
			currentColIndex: number,
			direction: "up" | "down" | "left" | "right",
		): { rowIndex: number; colIndex: number } | null => {
			const editableColumns = getEditableColumns();
			if (editableColumns.length === 0) return null;

			const currentRow = filteredData[currentRowIndex];
			const currentEventId = currentRow?.EventId;

			let currentEditableColIdx = editableColumns.findIndex(
				(ec) => ec.index === currentColIndex,
			);

			if (currentEditableColIdx === -1) {
				// Current column is not editable, find the first editable one
				currentEditableColIdx = 0;
			}

			// For left/right, stay on the same row
			if (direction === "left") {
				if (currentEditableColIdx > 0) {
					const firstRowIdx = findFirstRowOfEvent(currentEventId);
					return {
						rowIndex: firstRowIdx !== -1 ? firstRowIdx : currentRowIndex,
						colIndex: editableColumns[currentEditableColIdx - 1].index,
					};
				}
				return null;
			}

			if (direction === "right") {
				if (currentEditableColIdx < editableColumns.length - 1) {
					const firstRowIdx = findFirstRowOfEvent(currentEventId);
					return {
						rowIndex: firstRowIdx !== -1 ? firstRowIdx : currentRowIndex,
						colIndex: editableColumns[currentEditableColIdx + 1].index,
					};
				}
				return null;
			}

			// For up/down, navigate to the next/previous unique EventId
			const col = visibleColumns[currentColIndex];
			const step = direction === "up" ? -1 : 1;

			const seenEventIds = new Set<number>([currentEventId]);

			for (
				let i = currentRowIndex + step;
				i >= 0 && i < filteredData.length;
				i += step
			) {
				const r = filteredData[i];
				if (!r) continue;

				// Skip rows with the same EventId or already seen events
				if (seenEventIds.has(r.EventId)) continue;

				// Found a different event - get the first row of this contiguous block
				const firstRowOfBlock = findFirstRowOfBlock(r.EventId, i);
				const firstRow = filteredData[firstRowOfBlock];

				// Check if this cell is editable on the first row of the block
				if (col && firstRow && isCellEditable(firstRow, col)) {
					return { rowIndex: firstRowOfBlock, colIndex: currentColIndex };
				}

				seenEventIds.add(r.EventId);
			}

			return null;
		},
		[
			filteredData,
			visibleColumns,
			getEditableColumns,
			isCellEditable,
			findFirstRowOfEvent,
			findFirstRowOfBlock,
		],
	);

	// Single click - select cell
	const handleCellClick = useCallback(
		(
			row: ProcessedRow,
			col: ColumnDef,
			rowKey: string,
			rowIndex: number,
			colIndex: number,
		) => {
			// Always use the first row of the event for spanned cells
			const firstRowIdx = findFirstRowOfEvent(row.EventId);
			const effectiveRowIndex = firstRowIdx !== -1 ? firstRowIdx : rowIndex;
			const effectiveRow = filteredData[effectiveRowIndex] || row;
			const effectiveRowKey = `${effectiveRow.EventId}-${effectiveRow.RowIndex ?? 0}`;

			if (!isCellEditable(effectiveRow, col)) return;

			// If clicking on the same cell that's already selected, do nothing
			if (
				selectedCell?.rowIndex === effectiveRowIndex &&
				selectedCell?.colIndex === colIndex
			) {
				return;
			}

			setSelectedCell({
				rowIndex: effectiveRowIndex,
				colIndex,
				rowKey: effectiveRowKey,
				field: col.field,
			});
			setEditingCell(null);
		},
		[selectedCell, isCellEditable, findFirstRowOfEvent, filteredData],
	);

	// Double click - enter edit mode
	const handleCellDoubleClick = useCallback(
		(row: ProcessedRow, col: ColumnDef, rowKey: string) => {
			// Always use the first row of the event for spanned cells
			const firstRowIdx = findFirstRowOfEvent(row.EventId);
			const effectiveRow =
				firstRowIdx !== -1 ? filteredData[firstRowIdx] || row : row;
			const effectiveRowKey = `${effectiveRow.EventId}-${effectiveRow.RowIndex ?? 0}`;

			if (!isCellEditable(effectiveRow, col)) return;

			const value = col.getValue
				? col.getValue(effectiveRow)
				: (effectiveRow as any)[col.field];
			const displayValue = col.formatValue
				? String(value ?? "").replace("%", "")
				: String(value ?? "");

			setEditingCell({
				rowKey: effectiveRowKey,
				field: col.field,
				value: displayValue,
			});
		},
		[isCellEditable, findFirstRowOfEvent, filteredData],
	);

	const enterEditMode = useCallback(() => {
		if (!selectedCell) return;

		const row = filteredData[selectedCell.rowIndex];
		const col = visibleColumns[selectedCell.colIndex];

		if (!row || !col || !isCellEditable(row, col)) return;

		const value = col.getValue ? col.getValue(row) : (row as any)[col.field];
		const displayValue = col.formatValue
			? String(value ?? "").replace("%", "")
			: String(value ?? "");

		setEditingCell({
			rowKey: selectedCell.rowKey,
			field: col.field,
			value: displayValue,
		});
	}, [selectedCell, filteredData, visibleColumns, isCellEditable]);

	const handleCellChange = useCallback((value: string) => {
		setEditingCell((prev) => (prev ? { ...prev, value } : null));
	}, []);

	const handleCellBlur = useCallback(async () => {
		if (!editingCell) return;

		const [eventIdStr] = editingCell.rowKey.split("-");
		const eventId = parseInt(eventIdStr);
		const row = filteredData.find(
			(r) =>
				r.EventId === eventId &&
				`${r.EventId}-${r.RowIndex ?? 0}` === editingCell.rowKey,
		);

		if (!row) {
			setEditingCell(null);
			return;
		}

		const col = visibleColumns.find((c) => c.field === editingCell.field);
		if (!col) {
			setEditingCell(null);
			return;
		}

		const parsedValue = col.parseValue
			? col.parseValue(editingCell.value)
			: editingCell.value;

		if (col.field === "BudgetTPH" && !isValidBudgetTPHValue(parsedValue)) {
			setEditingCell(null);
			return;
		}

		await onCellUpdate({
			eventId: row.EventId,
			start: row.OriginalStart || row.Start,
			end: row.OriginalEnd || row.End,
			sectionId: row.SectionId ?? null,
			field: col.field as keyof Event,
			value: parsedValue,
			row,
		});

		setEditingCell(null);
	}, [editingCell, filteredData, visibleColumns, onCellUpdate]);

	const handleEditKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleCellBlur();
			} else if (e.key === "Escape") {
				e.preventDefault();
				setEditingCell(null);
			} else if (e.key === "Tab") {
				e.preventDefault();
				handleCellBlur();
				// Move to next cell after blur
				if (selectedCell) {
					const next = findNextEditableCell(
						selectedCell.rowIndex,
						selectedCell.colIndex,
						e.shiftKey ? "left" : "right",
					);
					if (next) {
						const row = filteredData[next.rowIndex];
						const col = visibleColumns[next.colIndex];
						if (row && col) {
							setSelectedCell({
								rowIndex: next.rowIndex,
								colIndex: next.colIndex,
								rowKey: `${row.EventId}-${row.RowIndex ?? 0}`,
								field: col.field,
							});
						}
					}
				}
			}
		},
		[
			handleCellBlur,
			selectedCell,
			findNextEditableCell,
			filteredData,
			visibleColumns,
		],
	);

	// Handle click outside to clear selection
	useEffect(() => {
		if (!selectedCell) return;

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			// Check if click is on a table cell with data-cell-id attribute
			const isTableCell = target.closest("[data-cell-id]");
			if (!isTableCell) {
				setSelectedCell(null);
				setEditingCell(null);
			}
		};

		// Use capture phase to detect clicks before they're stopped by stopPropagation
		document.addEventListener("click", handleClickOutside, true);
		document.addEventListener("contextmenu", handleClickOutside, true);

		return () => {
			document.removeEventListener("click", handleClickOutside, true);
			document.removeEventListener("contextmenu", handleClickOutside, true);
		};
	}, [selectedCell]);

	// Handle keyboard events for navigation (when cell is selected but not editing)
	useEffect(() => {
		if (!selectedCell || editingCell) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			const { key } = e;

			if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) {
				e.preventDefault();
				const direction = key.replace("Arrow", "").toLowerCase() as
					| "up"
					| "down"
					| "left"
					| "right";
				const next = findNextEditableCell(
					selectedCell.rowIndex,
					selectedCell.colIndex,
					direction,
				);
				if (next) {
					const row = filteredData[next.rowIndex];
					const col = visibleColumns[next.colIndex];
					if (row && col) {
						setSelectedCell({
							rowIndex: next.rowIndex,
							colIndex: next.colIndex,
							rowKey: `${row.EventId}-${row.RowIndex ?? 0}`,
							field: col.field,
						});
					}
				}
			}

			if (key === "Enter" || key === "F2") {
				e.preventDefault();
				enterEditMode();
			}

			// Tab navigation (same functionality as keyright)
			if (key === "Tab") {
				e.preventDefault();
				const next = findNextEditableCell(
					selectedCell.rowIndex,
					selectedCell.colIndex,
					e.shiftKey ? "left" : "right",
				);
				if (next) {
					const row = filteredData[next.rowIndex];
					const col = visibleColumns[next.colIndex];
					if (row && col) {
						setSelectedCell({
							rowIndex: next.rowIndex,
							colIndex: next.colIndex,
							rowKey: `${row.EventId}-${row.RowIndex ?? 0}`,
							field: col.field,
						});
					}
				}
			}

			if (key === "Escape") {
				e.preventDefault();
				setSelectedCell(null);
			}

			// Start typing to enter edit mode (alphanumeric keys)
			if (key.length === 1 && /^[a-zA-Z0-9]$/.test(key)) {
				e.preventDefault();
				const row = filteredData[selectedCell.rowIndex];
				const col = visibleColumns[selectedCell.colIndex];
				if (row && col && isCellEditable(row, col)) {
					setEditingCell({
						rowKey: selectedCell.rowKey,
						field: col.field,
						value: key,
					});
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [
		selectedCell,
		editingCell,
		findNextEditableCell,
		enterEditMode,
		filteredData,
		visibleColumns,
		isCellEditable,
	]);

	const cancelEditing = useCallback(() => {
		setEditingCell(null);
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedCell(null);
		setEditingCell(null);
	}, []);

	return {
		editingCell,
		selectedCell,
		handleCellClick,
		handleCellDoubleClick,
		handleCellChange,
		handleCellBlur,
		handleEditKeyDown,
		cancelEditing,
		clearSelection,
	};
};
